import { observer } from 'owa-mobx-react';
import { otherViewFilterText } from 'owa-locstrings/lib/strings/otherviewfiltertext.locstring.json';
import { focusedViewFilterText } from 'owa-locstrings/lib/strings/focusedviewfiltertext.locstring.json';
import loc from 'owa-localize';
import React from 'react';
import Droppable from 'owa-dnd/lib/components/Droppable';
import type FocusedViewFilter from 'owa-service/lib/contract/FocusedViewFilter';
import { selectFocusedViewFilter } from 'owa-mail-triage-table-utils';
import type { DragData } from 'owa-dnd/lib/utils/dragDataUtil';
import { Tab, TabList, type SelectTabData, type SelectTabEvent } from '@fluentui/react-components';
import {
    getFocusedOtherDropViewState,
    getFocusedFilterForTable,
    getStore as getListViewStore,
} from 'owa-mail-list-store';
import { getDensityModeCssClass } from 'owa-fabric-theme';
import { useComputedValue } from 'owa-react-hooks/lib/useComputed';

import {
    full,
    medium,
    compact,
    tabItemContainer,
    dropContainer,
    dropHoverTreatment,
    tabListRoot,
    tabListContainer,
    tab,
    tabText,
} from './InboxPivotStyles.scss';

import classnames from 'owa-classnames';

export interface FocusedInboxPivotProps {
    tableViewId: string;
    onDropFocused: (dragInfo: DragData) => void;
    onDropOther: (dragInfo: DragData) => void;
    onKeyDown: (ev: React.KeyboardEvent<HTMLElement>, tableViewId: string) => void;
    isSupportedItemTypeForDragDrop: boolean;
    shouldShowOnDropHoverTreatment: boolean;
}

const defaultExport: React.FunctionComponent<FocusedInboxPivotProps> = observer(
    function FocusedInboxPivot(props: FocusedInboxPivotProps) {
        const tableView = getListViewStore().tableViews.get(props.tableViewId);
        const densityModeCssClass = getDensityModeCssClass(full, medium, compact);

        const tabContentProps = useComputedValue(
            () => ({
                className: classnames(tabText, densityModeCssClass),
            }),
            [densityModeCssClass]
        );

        const onTabSelect = React.useCallback((_ev: SelectTabEvent, data: SelectTabData) => {
            const key = data.value as string;
            selectFocusedViewFilter(convertPivotKeyToFocusedViewFilter(key), 'FocusedOtherPivot');
        }, []);
        /**
         * Convert the focused view filter to a string that used as the key in Pivot fabric component
         * We need to do this instead of just using the FocusedViewFilter value, because FocusedViewFilter.None(-1) is ignored by the Fabric Pivot
         */
        const getKeyForSelectedFocusedViewFilter = (): string => {
            const focusedFilterForTable = getFocusedFilterForTable(tableView);
            let selectedKey = '';
            switch (focusedFilterForTable) {
                case -1:
                    selectedKey = 'inbox';
                    break;
                case 0:
                    selectedKey = 'focused';
                    break;
                case 1:
                    selectedKey = 'other';
                    break;
            }
            return selectedKey;
        };
        /**
         * Convert pivot key string back to the focused view filter to select
         * @param key of the pivot component
         */
        const convertPivotKeyToFocusedViewFilter = (key: string) => {
            switch (key) {
                case 'focused':
                    return 0;
                case 'other':
                    return 1;
                default:
                case 'inbox':
                    return -1;
            }
        };

        const onKeyDownHandler = React.useCallback(
            (ev: React.KeyboardEvent<HTMLElement>) => {
                props.onKeyDown(ev, props.tableViewId);
            },
            [props.onKeyDown, props.tableViewId]
        );

        const selectedKey = getKeyForSelectedFocusedViewFilter();

        const renderDroppableTab = (
            tabKey: string,
            label: string,
            onDrop: (dragInfo: DragData) => void
        ): JSX.Element => {
            const isDropTarget = tabKey !== selectedKey && props.isSupportedItemTypeForDragDrop;
            const droppableStyles = classnames(
                tabItemContainer,
                isDropTarget && dropContainer,
                props.shouldShowOnDropHoverTreatment && dropHoverTreatment,
                densityModeCssClass
            );
            return (
                <Droppable
                    classNames={droppableStyles}
                    dropViewState={getFocusedOtherDropViewState()}
                    onDrop={onDrop}
                >
                    <Tab value={tabKey} className={tab} content={tabContentProps}>
                        {label}
                    </Tab>
                </Droppable>
            );
        };

        return (
            <div
                className={classnames(tabListContainer, densityModeCssClass)}
                onKeyDown={onKeyDownHandler}
            >
                <TabList
                    className={classnames(tabListRoot, densityModeCssClass)}
                    selectedValue={selectedKey}
                    onTabSelect={onTabSelect}
                >
                    {renderDroppableTab('focused', loc(focusedViewFilterText), props.onDropFocused)}
                    {renderDroppableTab('other', loc(otherViewFilterText), props.onDropOther)}
                </TabList>
            </div>
        );
    },
    'FocusedInboxPivot'
);
export default defaultExport;
